import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, TextField, Button, Tooltip,
  MenuItem, Divider
} from '@mui/material';
import { Home, Tune, Save, DeleteOutline, Add } from '@mui/icons-material';
import axios from 'axios';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import ConfirmDialog from './ConfirmDialog';

const API = process.env.REACT_APP_API_URL;

const LABEL_MAP = {
  AR_ACCOUNT:                   'Student Debtors (AR)',
  STUDENT_ADVANCE_ACCOUNT:      'Student Advance Receipts (Liability)',
  REQUISITION_EXPENSE_ACCOUNT:  'Store Issue / Consumption (Expense)',
};

const BLANK_NEW = { key: '', description: '', value: '' };

const PostingSetupPage = () => {
  const navigate = useNavigate();
  const { accounts: glAccounts } = useGLAccounts();
  const [setups,   setSetups]   = useState([]);
  const [edits,    setEdits]    = useState({});
  const [saving,   setSaving]   = useState({});
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [newSetup, setNewSetup] = useState(BLANK_NEW);
  const [adding,   setAdding]   = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // key pending deletion
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  const fetchSetups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/academics/posting-setup`, { headers });
      setSetups(res.data);
      const init = {};
      res.data.forEach(s => { init[s.key] = s.value; });
      setEdits(init);
    } catch { setError('Failed to load posting setup'); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchSetups(); }, [fetchSetups]);

  const handleSave = async (key) => {
    setSaving(s => ({ ...s, [key]: true })); setError('');
    try {
      await axios.put(`${API}/api/academics/posting-setup/${key}`, { value: edits[key] }, { headers });
      flash(`${key} saved`);
      fetchSetups();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  const handleAdd = async () => {
    if (!newSetup.key.trim())   return setError('Setup key is required');
    if (!newSetup.value)        return setError('Pick a GL account for the new setup');
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/api/academics/posting-setup`, newSetup, { headers });
      flash(`${newSetup.key.toUpperCase().replace(/\s+/g, '_')} added`);
      setNewSetup(BLANK_NEW);
      fetchSetups();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add setup');
    } finally { setAdding(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true); setError('');
    try {
      await axios.delete(`${API}/api/academics/posting-setup/${confirmDelete}`, { headers });
      flash(`${confirmDelete} deleted`);
      setConfirmDelete(null);
      fetchSetups();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    } finally { setDeleting(false); }
  };

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Tune color="primary" />
              <Typography variant="h5" color="primary">Posting Setup</Typography>
            </Box>
            <Tooltip title="Back to Setups">
              <IconButton onClick={() => navigate('/setups')}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Map transaction types to GL accounts — no account is hardcoded. Changes take effect
            immediately on new transactions.
          </Typography>

          {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Setup Key</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell sx={{ width: 260 }}><strong>GL Account</strong></TableCell>
                <TableCell sx={{ width: 120 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {setups.map(s => (
                <TableRow key={s.key} hover>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={600}>
                      {s.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{LABEL_MAP[s.key] || s.label || s.key}</Typography>
                    {s.description && (
                      <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" fullWidth value={edits[s.key] || ''}
                      onChange={e => setEdits(ed => ({ ...ed, [s.key]: e.target.value }))}>
                      {edits[s.key] && !glAccounts.find(a => a.accountNo === edits[s.key]) && (
                        <MenuItem value={edits[s.key]}>{edits[s.key]} (current)</MenuItem>
                      )}
                      {glAccountOptions(glAccounts, { includeNone: false })}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Button size="small" variant="contained" startIcon={<Save />}
                        disabled={saving[s.key] || edits[s.key] === s.value}
                        onClick={() => handleSave(s.key)}>
                        {saving[s.key] ? '…' : 'Save'}
                      </Button>
                      <Tooltip title="Delete setup">
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(s.key)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {setups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No posting setups yet — add one below.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />

          {/* Add a new posting setup */}
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Add posting setup</Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="flex-start">
            <TextField size="small" label="Key" placeholder="e.g. AP_ACCOUNT"
              value={newSetup.key}
              onChange={e => setNewSetup(n => ({ ...n, key: e.target.value }))}
              sx={{ width: 180 }}
              helperText="UPPER_SNAKE_CASE" />
            <TextField size="small" label="Description" placeholder="What this maps"
              value={newSetup.description}
              onChange={e => setNewSetup(n => ({ ...n, description: e.target.value }))}
              sx={{ flex: 1, minWidth: 160 }} />
            <TextField select size="small" label="GL Account" value={newSetup.value}
              onChange={e => setNewSetup(n => ({ ...n, value: e.target.value }))}
              sx={{ width: 240 }}>
              {glAccountOptions(glAccounts, { includeNone: false })}
            </TextField>
            <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
              disabled={adding} sx={{ mt: 0.5 }}>
              {adding ? 'Adding…' : 'Add'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete posting setup?"
        message={`Remove the "${confirmDelete}" mapping? Transactions that rely on it will fall back to defaults until you re-add it.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
};

export default PostingSetupPage;
